<script setup lang="ts">
import { computed } from 'vue'
import type {
  TechnicalPassportDimensions,
  TechnicalPassportElement,
  TechnicalPassportYarn,
} from '../types'
import { decorationTypeFromElement, type SockDecorationType } from '../sockDecorations'
import type { SockContourGeometry } from './sockContourGeometry'

const props = withDefaults(defineProps<{
  elements?: TechnicalPassportElement[]
  yarns?: TechnicalPassportYarn[]
  dimensions?: TechnicalPassportDimensions
  geometry: SockContourGeometry
  selectedIndex?: number
  interactive?: boolean
}>(), {
  elements: () => [],
  yarns: () => [],
  selectedIndex: -1,
  interactive: false,
})

const emit = defineEmits<{ select: [index: number] }>()

function normalizeColor(value?: string): string | undefined {
  const color = value?.trim()
  return color && /^#[0-9a-f]{6}$/i.test(color) ? color : undefined
}

function elementColor(element: TechnicalPassportElement): string {
  const role = element.yarn_role?.toLowerCase()
  const yarn = props.yarns.find((item) => item.role?.toLowerCase() === role)
  return normalizeColor(yarn?.color_code) ?? '#9c3f72'
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value))
}

const decorations = computed(() => props.elements
  .map((element, index) => ({ element, index }))
  .map(({ element, index }) => ({ element, index, type: decorationTypeFromElement(element) }))
  .filter((item): item is { element: TechnicalPassportElement; index: number; type: SockDecorationType } =>
    Boolean(item.type) && Boolean(props.geometry.decorationAnchors[item.element.position_code ?? 'top_cuff']),
  )
  .map(({ element, index, type }) => ({ element, index, type, anchor: props.geometry.decorationAnchors[element.position_code ?? 'top_cuff']! }))
  .map(({ element, index, type, anchor }, decorationIndex, allDecorations) => {
    const position = element.position_code ?? 'top_cuff'
    const zoneOffset = allDecorations.slice(0, decorationIndex).filter((item) => item.element.position_code === position).length
    const cuffWidthMm = Number(props.dimensions?.cuff_width_mm ?? 80)
    const requestedSpan = Number(element.length_mm ?? cuffWidthMm)
    const spanRatio = clamp(requestedSpan / Math.max(cuffWidthMm, 1), type === 'fabric_loop_trim' ? .12 : .18, 1)
    const width = anchor.width * spanRatio
    const x = anchor.x + (anchor.width - width) / 2
    const pitchMm = clamp(Number(element.width_mm ?? 12), 4, 40)
    const count = clamp(Math.round(requestedSpan / pitchMm), 3, 18)
    const step = width / count
    const depth = clamp(Number(element.height_mm ?? 8) * .75, 4, Math.max(anchor.bandHeight - 3, 8))
    const y = anchor.y + 1 + zoneOffset * 2
    const height = Math.min(depth, anchor.bandHeight - 2)
    return {
      element,
      index,
      type,
      position,
      x,
      y,
      width,
      height,
      depth,
      count,
      step,
      rotation: anchor.rotation ?? 0,
      color: elementColor(element),
      centers: Array.from({ length: count }, (_, repeatIndex) => x + step * (repeatIndex + .5)),
    }
  }))

function trianglePath(item: typeof decorations.value[number]): string {
  let path = `M${item.x} ${item.y}`
  for (let index = 0; index < item.count; index += 1) {
    path += `l${item.step / 2} ${item.depth}l${item.step / 2} ${-item.depth}`
  }
  return path
}

function scallopPath(item: typeof decorations.value[number], offset = 0): string {
  let path = `M${item.x} ${item.y + offset}`
  for (let index = 0; index < item.count; index += 1) {
    path += `q${item.step / 2} ${item.depth} ${item.step} 0`
  }
  return path
}
</script>

<template>
  <g class="sock-decoration-layer" aria-label="Sock decorations">
    <g
      v-for="item in decorations"
      :key="`${item.index}-${item.type}`"
      class="sock-decoration"
      :class="[`decoration-${item.type.replaceAll('_', '-')}`, { selected: item.index === selectedIndex, interactive }]"
      :data-decoration="item.type"
      :data-zone="item.position"
      :data-element-index="item.index"
      :style="{ '--decoration-color': item.color }"
      :transform="item.rotation ? `rotate(${item.rotation} ${item.x} ${item.y})` : undefined"
      role="img"
      @click.stop="interactive && emit('select', item.index)"
    >
      <template v-if="item.type === 'lace_trim'">
        <path class="decoration-band" :d="`M${item.x} ${item.y + 2}H${item.x + item.width}`"/>
        <path class="decoration-line decoration-line--soft" :d="scallopPath(item, 2)"/>
        <circle v-for="center in item.centers" :key="center" class="lace-eye" :cx="center" :cy="item.y + item.depth * .58 + 2" :r="Math.max(1.2, item.step * .12)"/>
      </template>
      <path v-else-if="item.type === 'picot_edge'" class="decoration-line" :d="trianglePath(item)"/>
      <path v-else-if="item.type === 'scalloped_edge'" class="decoration-line decoration-line--soft" :d="scallopPath(item)"/>
      <template v-else-if="item.type === 'double_layered_ruffle'">
        <path class="ruffle-fill" :d="`${scallopPath(item, 1)}V${item.y + 4}H${item.x}Z`"/>
        <path class="decoration-line decoration-line--soft" :d="scallopPath(item, 1)"/>
        <path class="decoration-line decoration-line--soft ruffle-second" :d="scallopPath(item, Math.min(item.depth * .65, 8))"/>
      </template>
      <template v-else-if="item.type === 'fabric_loop_trim'">
        <path class="loop-fill" :d="`M${item.x + item.width - item.step * 1.8} ${item.y + 3}C${item.x + item.width - item.step * 1.5} ${item.y + item.depth * 1.6} ${item.x + item.width - item.step * .35} ${item.y + item.depth * 1.6} ${item.x + item.width - item.step * .2} ${item.y + 3}Z`"/>
        <path class="decoration-line" :d="`M${item.x + item.width - item.step * 1.65} ${item.y + 3}C${item.x + item.width - item.step * 1.4} ${item.y + item.depth * 1.25} ${item.x + item.width - item.step * .55} ${item.y + item.depth * 1.25} ${item.x + item.width - item.step * .35} ${item.y + 3}`"/>
      </template>
      <template v-else-if="item.type === 'folded_over_cuff'">
        <rect class="fold-fill" :x="item.x" :y="item.y" :width="item.width" :height="item.height" rx="1"/>
        <path class="fold-rib" :d="`M${item.x} ${item.y + item.height}H${item.x + item.width}`"/>
        <path class="fold-highlight" :d="`M${item.x + 3} ${item.y + 3}H${item.x + item.width - 3}`"/>
      </template>
      <template v-else-if="item.type === 'bow_detail'">
        <path class="bow-fill" :d="`M${item.x + item.width / 2} ${item.y + item.height / 2}C${item.x + 3} ${item.y - 3} ${item.x + 3} ${item.y + item.height + 3} ${item.x + item.width / 2} ${item.y + item.height / 2}C${item.x + item.width - 3} ${item.y - 3} ${item.x + item.width - 3} ${item.y + item.height + 3} ${item.x + item.width / 2} ${item.y + item.height / 2}Z`"/>
        <circle class="decoration-node" :cx="item.x + item.width / 2" :cy="item.y + item.height / 2" :r="3"/>
      </template>
      <template v-else-if="item.type === 'embroidery_motif'">
        <path class="embroidery-stem" :d="`M${item.x + item.width / 2} ${item.y + item.height}V${item.y + 5}`"/>
        <circle v-for="center in item.centers.slice(0, 5)" :key="center" class="embroidery-dot" :cx="center" :cy="item.y + item.height / 2 + Math.sin(center) * 5" r="2.2"/>
      </template>
      <template v-else-if="item.type === 'contrast_piping'">
        <path class="piping-line" :d="`M${item.x} ${item.y + item.height / 2}H${item.x + item.width}`"/>
        <path class="piping-highlight" :d="`M${item.x} ${item.y + item.height / 2 - 2}H${item.x + item.width}`"/>
      </template>
      <template v-else-if="item.type === 'applique_patch'">
        <rect class="patch-fill" :x="item.x" :y="item.y" :width="item.width" :height="item.height" rx="3"/>
        <path class="patch-stitch" :d="`M${item.x + 3} ${item.y + 3}H${item.x + item.width - 3}V${item.y + item.height - 3}H${item.x + 3}Z`"/>
      </template>
      <template v-else-if="item.type === 'bead_trim'">
        <circle v-for="center in item.centers" :key="center" class="bead" :cx="center" :cy="item.y + item.height / 2" :r="Math.max(1.8, item.step * .16)"/>
      </template>
      <template v-else-if="item.type === 'silicone_grip_dots'">
        <g v-for="center in item.centers" :key="center">
          <circle class="grip-dot" :cx="center" :cy="item.y + 4" r="2"/>
          <circle class="grip-dot" :cx="center + item.step * .35" :cy="item.y + item.height - 3" r="2"/>
        </g>
      </template>
    </g>
  </g>
</template>

<style scoped>
.sock-decoration{--decoration-color:#9c3f72;pointer-events:none}.sock-decoration.interactive{cursor:pointer;pointer-events:all}.decoration-line,.decoration-band,.fold-rib,.fold-highlight,.embroidery-stem,.piping-line,.piping-highlight,.patch-stitch{fill:none;stroke:var(--decoration-color);stroke-linecap:round;stroke-linejoin:round}.decoration-line{stroke-width:2.2}.decoration-line--soft{stroke-width:1.9}.decoration-band{stroke-width:3.2}.lace-eye{fill:#fff;stroke:var(--decoration-color);stroke-width:1}.ruffle-fill,.loop-fill,.fold-fill,.bow-fill,.patch-fill{fill:color-mix(in srgb,var(--decoration-color) 32%,white);stroke:var(--decoration-color);stroke-width:1}.ruffle-second{opacity:.78}.fold-rib{stroke-width:1.6;stroke-dasharray:3 2}.fold-highlight,.piping-highlight{stroke:#fff;stroke-width:1;opacity:.75}.bow-fill{stroke-width:1.4}.decoration-node,.embroidery-dot,.bead{fill:#fff;stroke:var(--decoration-color);stroke-width:1.2}.embroidery-stem{stroke-width:1.4}.piping-line{stroke-width:4}.patch-stitch{stroke-width:1;stroke-dasharray:2 2;opacity:.75}.bead{filter:drop-shadow(0 1px 1px rgb(23 52 84/.2))}.grip-dot{fill:color-mix(in srgb,var(--decoration-color) 68%,white);stroke:#fff;stroke-width:.7}.sock-decoration.selected{filter:drop-shadow(0 0 2px #07996f)}.sock-decoration.selected .decoration-line,.sock-decoration.selected .decoration-band,.sock-decoration.selected .fold-rib,.sock-decoration.selected .piping-line{stroke:#07996f;stroke-width:3}.sock-decoration.selected .ruffle-fill,.sock-decoration.selected .loop-fill,.sock-decoration.selected .fold-fill,.sock-decoration.selected .bow-fill,.sock-decoration.selected .patch-fill{stroke:#07996f}
</style>
